import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { LogoMark } from "../components/Logo";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { About } from "../components/About";
import { Activities } from "../components/Activities";
import { Bring } from "../components/Bring";
import { Facilitator } from "../components/Facilitator";
import { Faq } from "../components/Faq";
import { RegistrationModal } from "../components/RegistrationModal";

const NAV = [
  ["About", "#about"],
  ["Activities", "#activities"],
  ["What to bring", "#bring"],
  ["Facilitator", "#facilitator"],
  ["FAQ", "#faq"],
];

export default function Landing() {
  const [config, setConfig] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showCta, setShowCta] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
    const onScroll = () => setShowCta(window.scrollY > 750);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openModal = () => setModalOpen(true);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#262320]" data-testid="landing-page">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-[#D9C3A3]/40 bg-[#FAF7F2]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-8 lg:px-12">
          <a href="#top" className="flex items-center gap-3">
            <LogoMark />
            <span className="font-serif text-lg tracking-wide text-[#262320]">
              {config?.workshop_title || "Soma & Soul"}
            </span>
          </a>
          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map(([label, href]) => (
              <a
                key={href}
                href={href}
                className="text-xs uppercase tracking-[0.18em] text-[#6E6862] transition-colors hover:text-[#5E7D6A]"
              >
                {label}
              </a>
            ))}
          </nav>
          <button
            data-testid="nav-reserve-button"
            onClick={openModal}
            className="rounded-full bg-[#262320] px-6 py-2.5 text-xs font-medium tracking-wide text-[#FAF7F2] transition-colors hover:bg-[#5E7D6A]"
          >
            Reserve Your Spot
          </button>
        </div>
      </header>

      <main id="top">
        <Hero config={config} onReserve={openModal} />
        <Marquee />
        <About />
        <Activities />
        <Bring />
        <Facilitator config={config} />

        <section className="relative overflow-hidden py-24 lg:py-36" data-testid="closing-cta">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E8E3F0] blur-3xl opacity-70" />
          <div className="relative mx-auto max-w-3xl px-6 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-3xl font-light tracking-tight text-[#262320] sm:text-4xl lg:text-5xl"
            >
              The circle is small, and it is <span className="italic text-[#5E7D6A]">waiting for you</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={openModal}
                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#5E7D6A] px-10 py-4 text-sm font-medium tracking-wide text-[#FAF7F2] shadow-lg shadow-[#5E7D6A]/25 transition-[background-color,transform] duration-300 hover:bg-[#4d6a59] hover:-translate-y-0.5"
                data-testid="closing-reserve-button"
              >
                Reserve Your Spot
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <p className="mt-5 text-xs text-[#6E6862]">
                {config?.date || "Date to be announced"} · {config?.time || ""} ·{" "}
                {config?.price_inr ? `₹${config.price_inr.toLocaleString("en-IN")}` : "Price TBA"}
              </p>
            </motion.div>
          </div>
        </section>

        <Faq config={config} />
      </main>

      <footer className="border-t border-[#D9C3A3]/50 bg-[#F5F0E8] py-12">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 sm:px-8 md:flex-row lg:px-12">
          <div className="flex items-center gap-3">
            <LogoMark size={28} />
            <span className="font-serif text-base text-[#262320]">{config?.workshop_title || "Soma & Soul"}</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[#6E6862]">
            <Link to="/privacy" className="transition-colors hover:text-[#5E7D6A]" data-testid="privacy-link">
              Privacy Policy
            </Link>
            <Link to="/admin" className="transition-colors hover:text-[#5E7D6A]" data-testid="admin-access-button">
              Facilitator Login
            </Link>
          </div>
          <p className="text-xs text-[#6E6862]">© {new Date().getFullYear()} {config?.workshop_title || "Soma & Soul"}. All rights reserved.</p>
        </div>
      </footer>

      <AnimatePresence>
        {showCta && !modalOpen && (
          <motion.button
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={openModal}
            data-testid="sticky-cta-button"
            className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 rounded-full bg-[#262320] px-8 py-3.5 text-sm font-medium text-[#FAF7F2] shadow-2xl shadow-[#262320]/30 transition-colors hover:bg-[#5E7D6A]"
          >
            Reserve Your Spot {config?.price_inr ? `· ₹${config.price_inr.toLocaleString("en-IN")}` : ""}
          </motion.button>
        )}
      </AnimatePresence>

      <RegistrationModal open={modalOpen} onOpenChange={setModalOpen} config={config} />
    </div>
  );
}
